import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Chip,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { FC, useState } from 'react'
import { CabinetSizesSubType } from '../../../api/appAPI'
import useUnit from '../../../hooks/useUnit'
import CategorySizeAllValuesWithUnitSupport, {
  InputWithUnitSupport,
  cmToIn,
} from '../../common/UnitComponents'

interface INumberArray {
  name: string
  values: number[]
  adornment?: string

  setValues(v: number[]): void
}

export const NumberArray: FC<INumberArray> = (props) => {
  const [editMode, setEditMode] = useState(false)

  const [currentValue, setCurrentValue] = useState(0)

  const handleValueBlur = () => {
    const newValue = currentValue

    if (!props.values.includes(newValue)) {
      props.setValues([...props.values, newValue])
    }
  }

  const handleEditCancel = () => {
    setEditMode(false)
  }

  const handleEditApply = (valuesInCm: number[]) => {
    props.setValues(valuesInCm)

    setEditMode(false)
  }

  return (
    <>
      <Divider style={{ width: '80%', marginTop: 15, marginBottom: 5 }} />
      <Box display="flex">
        <Typography>{props.name}</Typography>
        <EditIcon
          fontSize="small"
          sx={{
            ml: 1,
            color: '#6c6c6c',
            cursor: 'pointer',
          }}
          onClick={() => setEditMode(true)}
        />
      </Box>
      <Box
        component="ul"
        px={1}
        my={1}
        style={{
          display: 'flex',
          justifyContent: 'center',
          columnGap: 10,
          rowGap: 10,
          flexWrap: 'wrap',
          listStyle: 'none',
        }}
      >
        {props.values
          .sort((a, b) => a - b)
          .map((value) => (
            <li key={value}>
              <Chip
                label={value}
                onDelete={() => {
                  props.setValues(props.values.filter((v) => v !== value))
                }}
              />
            </li>
          ))}
      </Box>

      {editMode ? (
        <CategorySizeAllValuesWithUnitSupport
          sx={{ mt: 2 }}
          width="100%"
          valuesInCm={props.values}
          handleEditCancel={handleEditCancel}
          handleEditApply={handleEditApply}
          isCMOnly
        />
      ) : (
        <FormControl variant="outlined" sx={{ mt: 2 }}>
          <InputLabel>{props.name}</InputLabel>
          <OutlinedInput
            style={{ width: '100%' }}
            type="number"
            label={props.name}
            value={currentValue}
            onChange={(e) => {
              const pervValue = currentValue
              let newValue = parseFloat(e.target.value)

              setCurrentValue(newValue)

              if (Math.abs(pervValue - newValue) === 1) handleValueBlur() // onBlur(newValue)
            }}
            onBlur={handleValueBlur}
            onKeyUp={(e) => {
              if (e.key === 'Enter') handleValueBlur()
            }}
            onFocus={(e) => e.target.select()}
            startAdornment={
              <InputAdornment position="start">
                {props.adornment}
              </InputAdornment>
            }
            inputProps={{ min: 0 }}
          />
        </FormControl>
      )}
    </>
  )
}

type PropsType<T> = {
  value: T
  onChange(event: React.ChangeEvent<{ value: T }>): void
}

type NumberArrayProps = {
  value: number[]
  onChange(value: number[]): void
}

interface IProps {
  title: string

  name: PropsType<string>
  extraDiscountStartPoint: PropsType<number>
  DPA_OfferCabinets: NumberArrayProps
  PPFD_OfferCabinets: NumberArrayProps
  DPA_OfferSlatwallAndAcc: NumberArrayProps
  PPFD_OfferSlatwallAndAcc: NumberArrayProps
  DPA_OfferOverheadStorage: NumberArrayProps
  PPFD_OfferOverheadStorage: NumberArrayProps

  onButtonClick(): void

  needSave?: boolean
}

const DiscountTierUIBox: FC<IProps> = (props) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      pb={2}
      style={{
        width: 400,
        height: 'fit-content',
        margin: 15,
        border: '1px solid black',
        maxHeight: '90%',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        py={1}
        style={{
          position: 'sticky',
          top: 0,
          width: '100%',
          backgroundColor: 'white',
          zIndex: 10,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={3}>
            {' '}
          </Grid>
          <Grid
            item
            xs={6}
            container
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="h6">{props.title}</Typography>
          </Grid>
          <Grid
            item
            xs={3}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            <Tooltip title="Save">
              <Fab
                color={props.needSave ? 'secondary' : 'primary'}
                size="small"
                onClick={props.onButtonClick}
                style={{ marginRight: 10 }}
              >
                <SaveIcon />
              </Fab>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <TextField
        label="Tier Name"
        variant="outlined"
        value={props.name.value}
        onChange={props.name.onChange}
        style={{ width: '60%', marginTop: 15 }}
      />

      <FormControl variant="outlined" style={{ width: '60%', marginTop: 15 }}>
        <InputLabel>Extra Discount Start Point</InputLabel>
        <OutlinedInput
          label="Extra Discount Start Point"
          startAdornment={<InputAdornment position="start">$</InputAdornment>}
          type="number"
          value={props.extraDiscountStartPoint.value}
          // @ts-ignore
          onChange={props.extraDiscountStartPoint.onChange}
        />
      </FormControl>

      <NumberArray
        name="DPA Offer Cabinets"
        values={props.DPA_OfferCabinets.value}
        setValues={props.DPA_OfferCabinets.onChange}
        adornment="$"
      />

      <NumberArray
        name="PPFD Offer Cabinets"
        values={props.PPFD_OfferCabinets.value}
        setValues={props.PPFD_OfferCabinets.onChange}
        adornment="%"
      />

      <NumberArray
        name="DPA Offer Slatwall And Acc"
        values={props.DPA_OfferSlatwallAndAcc.value}
        setValues={props.DPA_OfferSlatwallAndAcc.onChange}
        adornment="$"
      />

      <NumberArray
        name="PPFD Offer Slatwall And Acc"
        values={props.PPFD_OfferSlatwallAndAcc.value}
        setValues={props.PPFD_OfferSlatwallAndAcc.onChange}
        adornment="%"
      />

      <NumberArray
        name="DPA Offer Overhead Storage"
        values={props.DPA_OfferOverheadStorage.value}
        setValues={props.DPA_OfferOverheadStorage.onChange}
        adornment="$"
      />

      <NumberArray
        name="PPFD Offer Overhead Storage"
        values={props.PPFD_OfferOverheadStorage.value}
        setValues={props.PPFD_OfferOverheadStorage.onChange}
        adornment="%"
      />
    </Box>
  )
}

export default DiscountTierUIBox
